import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { MicrophoneError, MicrophoneErrorType } from 'web/hooks/audio/useAudioRecorder';

interface MicrophoneErrorDialogProps {
    error: MicrophoneError | null;
    onClose: () => void;
    isOpen: boolean;
    onStopAndSave?: () => void; // Optional callback to stop recording and save
    isHealthError?: boolean; // Flag to indicate this is a recording health error
}

export const MicrophoneErrorDialog: React.FC<MicrophoneErrorDialogProps> = ({
    error,
    onClose,
    isOpen,
    onStopAndSave,
    isHealthError = false
}) => {
    if (!isOpen || !error) return null;

    const getErrorTitle = () => {
        switch (error.type) {
            case MicrophoneErrorType.NOT_FOUND:
                return 'No Microphone Detected';
            case MicrophoneErrorType.PERMISSION_DENIED:
                return 'Microphone Access Denied';
            default:
                return 'Microphone Error';
        }
    };

    const getHelpText = () => {
        switch (error.type) {
            case MicrophoneErrorType.NOT_FOUND:
                return (
                    <div className="space-y-2">
                        <p>Please check that:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>A microphone is connected to your device</li>
                            <li>The microphone is properly plugged in</li>
                            <li>Your operating system recognizes the microphone</li>
                            <li>Try using an external USB microphone if available</li>
                        </ul>
                    </div>
                );
            case MicrophoneErrorType.PERMISSION_DENIED:
                return (
                    <div className="space-y-2">
                        <p>To allow microphone access:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Click the camera/microphone icon in your browser's address bar</li>
                            <li>Select "Allow" for microphone access</li>
                            <li>Refresh the page and try again</li>
                            <li>Check your browser settings to ensure this site has microphone permissions</li>
                        </ul>
                    </div>
                );
            default:
                return (
                    <div>
                        <p>An unexpected error occurred. Please try:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Refreshing the page</li>
                            <li>Using a different browser</li>
                            <li>Checking your microphone connection</li>
                            <li>Contact support if the issue persists</li>
                        </ul>
                    </div>
                );
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="microphone-error-modal" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                
                {/* Center modal */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <span className="sr-only">Close</span>
                            <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                        </button>
                    </div>
                    
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">{getErrorTitle()}</h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    {error.message}
                                </p>
                                <div className="mt-4 text-sm text-gray-700">
                                    {getHelpText()}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        {isHealthError && onStopAndSave ? (
                            <>
                                <button
                                    type="button"
                                    onClick={onStopAndSave}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Stop and Save Recording
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                                >
                                    Cancel Recording
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Got it
                                </button>
                                <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                                >
                                    Refresh Page
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}; 